import { useReducer } from 'react';
import { message } from 'antd';
import { txt } from 'libs/i18n';
import { getNameVariantOptions, getRoleOptions } from '../../speaker-utils';
const clearDefault = (items) => (items.map((o) => (Object.assign(Object.assign({}, o), { isDefault: false }))));
const clearDefaultEntries = (items) => (items.map((e) => (Object.assign(Object.assign({}, e), { nameVariant: Object.assign(Object.assign({}, e.nameVariant), { isDefault: false }) }))));
const reducer = (entries, action) => {
    switch (action.type) {
        case 'INIT':
            return action.entries;
        case 'ADD_NAME_VARIANT': {
            const { item } = action;
            const base = item.isDefault ? clearDefaultEntries(entries) : entries;
            const newEntry = { nameVariant: item, roles: [] };
            return item.isDefault ? [newEntry, ...base] : [...base, newEntry];
        }
        case 'EDIT_NAME_VARIANT': {
            const { newItem, originalItem } = action;
            const base = newItem.isDefault ? clearDefaultEntries(entries) : entries;
            return base.map((e) => (e.nameVariant.label === originalItem.label ? Object.assign(Object.assign({}, e), { nameVariant: newItem }) : e));
        }
        case 'DELETE_NAME_VARIANT':
            return entries.filter((e) => e.nameVariant.label !== action.label);
        case 'ADD_ROLE': {
            const { nameVariant, item } = action;
            return entries.map((e) => {
                if (e.nameVariant.label !== nameVariant.label)
                    return e;
                const base = item.isDefault ? clearDefault(e.roles) : e.roles;
                return Object.assign(Object.assign({}, e), { roles: item.isDefault ? [item, ...base] : [...base, item] });
            });
        }
        case 'EDIT_ROLE': {
            const { nameVariant, newItem, originalItem } = action;
            return entries.map((e) => {
                if (e.nameVariant.label !== nameVariant.label)
                    return e;
                const base = newItem.isDefault ? clearDefault(e.roles) : e.roles;
                return Object.assign(Object.assign({}, e), { roles: base.map((o) => (o.label === originalItem.label ? newItem : o)) });
            });
        }
        case 'DELETE_ROLE': {
            const { nameVariant, label } = action;
            return entries.map((e) => {
                if (e.nameVariant.label !== nameVariant.label)
                    return e;
                return Object.assign(Object.assign({}, e), { roles: e.roles.filter((o) => o.label !== label) });
            });
        }
        default: {
            const exhaustive = action;
            return exhaustive;
        }
    }
};
const useNameVariantEntries = (locale) => {
    const [entries, dispatch] = useReducer(reducer, []);
    const nameVariantOptions = entries.map((e) => e.nameVariant);
    const getRoles = (nameVariantLabel, documentRole) => {
        var _a, _b;
        const roles = (_b = (_a = entries.find((e) => e.nameVariant.label === nameVariantLabel)) === null || _a === void 0 ? void 0 : _a.roles) !== null && _b !== void 0 ? _b : [];
        if (documentRole == null) {
            return roles;
        }
        const docRoleIndex = roles.findIndex((r) => r.label === documentRole);
        if (docRoleIndex < 0) {
            return roles;
        }
        const docRole = Object.assign(Object.assign({}, roles[docRoleIndex]), { isFromDocument: true });
        if (docRoleIndex === 0) {
            return [docRole, ...roles.slice(1)];
        }
        return [docRole, ...roles.filter((_, i) => i !== docRoleIndex)];
    };
    const init = (speaker) => {
        if (speaker === null) {
            dispatch({ type: 'INIT', entries: [] });
            return;
        }
        const nameVariants = getNameVariantOptions(speaker, locale);
        dispatch({
            type: 'INIT',
            entries: nameVariants.map((nv) => {
                var _a, _b;
                return ({
                    nameVariant: nv,
                    roles: getRoleOptions(speaker, locale, (_a = nv.values) === null || _a === void 0 ? void 0 : _a.firstName, (_b = nv.values) === null || _b === void 0 ? void 0 : _b.lastName),
                });
            }),
        });
    };
    const addNameVariant = (item) => {
        if (entries.some((e) => e.nameVariant.label === item.label)) {
            void message.warning(txt('duplicateNameVariant'));
            return;
        }
        dispatch({ type: 'ADD_NAME_VARIANT', item });
    };
    const editNameVariant = (newItem, originalItem) => {
        const isDuplicate = newItem.label !== originalItem.label
            && entries.some((e) => e.nameVariant.label === newItem.label);
        if (isDuplicate) {
            void message.warning(txt('duplicateNameVariant'));
            return;
        }
        dispatch({ type: 'EDIT_NAME_VARIANT', newItem, originalItem });
    };
    const deleteNameVariant = (label) => {
        dispatch({ type: 'DELETE_NAME_VARIANT', label });
    };
    const addRole = (nameVariant, item) => {
        if (getRoles(nameVariant.label).some((o) => o.label === item.label)) {
            void message.warning(txt('duplicateRole'));
            return;
        }
        dispatch({ type: 'ADD_ROLE', nameVariant, item });
    };
    const editRole = (nameVariant, newItem, originalItem) => {
        const isDuplicate = newItem.label !== originalItem.label
            && getRoles(nameVariant.label).some((o) => o.label === newItem.label);
        if (isDuplicate) {
            void message.warning(txt('duplicateRole'));
            return;
        }
        dispatch({
            type: 'EDIT_ROLE', nameVariant, newItem, originalItem,
        });
    };
    const deleteRole = (nameVariant, label) => {
        dispatch({ type: 'DELETE_ROLE', nameVariant, label });
    };
    return {
        init,
        entries,
        nameVariant: {
            options: nameVariantOptions,
            add: addNameVariant,
            edit: editNameVariant,
            delete: deleteNameVariant,
        },
        role: {
            getRoles,
            add: addRole,
            edit: editRole,
            delete: deleteRole,
        },
    };
};
export default useNameVariantEntries;
